import type { IGuestRepository, CreateGuestDTO, UpdateGuestDTO, PaginatedResponse, QueryParams } from '@yawi/domain';
import { buildQueryString, DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@yawi/domain';
import type { Guest } from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

// Tipos de respuesta de la API
interface ApiGuestResponse {
  id: string;
  name: string;
  hostId: string;
  plateNumber?: string | null;
  validFrom: string;
  validTo: string;
  accessCode: string;
  numericCode: string;
  type: 'VISIT' | 'SERVICE' | 'DELIVERY' | 'PARTY';
  notes?: string | null;
  maxUses?: number | null;
  currentUses: number;
  isValid: boolean;
  createdAt: string;
  qrDataUrl?: string;
  qrCodeImage?: string;
  allowedDeviceIds?: string[];
}

interface ApiCreateGuestRequest {
  name: string;
  plateNumber?: string;
  validFrom: string;
  validTo: string;
  type: 'VISIT' | 'SERVICE' | 'DELIVERY' | 'PARTY';
  notes?: string;
  maxUses?: number;
  deviceIds?: string[];
}

// Mapea la respuesta de la API al tipo Guest interno
const mapApiGuestToGuest = (apiGuest: ApiGuestResponse): Guest => ({
  id: apiGuest.id,
  name: apiGuest.name,
  hostId: apiGuest.hostId,
  plateNumber: apiGuest.plateNumber || undefined,
  validFrom: apiGuest.validFrom,
  validTo: apiGuest.validTo,
  accessCode: apiGuest.accessCode,
  numericCode: apiGuest.numericCode,
  type: apiGuest.type,
  notes: apiGuest.notes || undefined,
  maxUses: apiGuest.maxUses || undefined,
  allowedDeviceIds: apiGuest.allowedDeviceIds,
  qrCodeImage: apiGuest.qrCodeImage || undefined,
});

export class ApiGuestRepository implements IGuestRepository {
  async getGuests(hostId: string): Promise<Guest[]> {
    const client = getApiClient();
    const response = await client.get<{ data: ApiGuestResponse[] }>('v1/guests/my-guests');
    return response.data.map(mapApiGuestToGuest);
  }

  async getGuestsPaginated(hostId: string, params?: QueryParams): Promise<PaginatedResponse<Guest>> {
    const client = getApiClient();
    const queryString = buildQueryString({
      page: params?.page ?? DEFAULT_PAGE,
      limit: params?.limit ?? DEFAULT_PAGE_SIZE,
      search: params?.search,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
      ...params,
    });
    const response = await client.get<PaginatedResponse<ApiGuestResponse>>(
      `v1/guests/my-guests?${queryString}`
    );
    return {
      ...response,
      data: response.data.map(mapApiGuestToGuest),
    };
  }

  async createGuest(guest: CreateGuestDTO): Promise<Guest> {
    const client = getApiClient();
    const request: ApiCreateGuestRequest = {
      name: guest.name,
      plateNumber: guest.plateNumber,
      validFrom: guest.validFrom,
      validTo: guest.validTo,
      type: guest.type,
      notes: guest.notes,
      maxUses: guest.maxUses,
      deviceIds: guest.deviceIds,
    };
    const apiGuest = await client.post<ApiGuestResponse>('v1/guests', request);
    return mapApiGuestToGuest(apiGuest);
  }

  async deleteGuest(guestId: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/guests/${guestId}`);
  }

  async getGuestById(id: string): Promise<Guest | null> {
    const client = getApiClient();
    try {
      const apiGuest = await client.get<ApiGuestResponse>(`v1/guests/${id}`);
      return mapApiGuestToGuest(apiGuest);
    } catch {
      return null;
    }
  }

  async updateGuest(guestId: string, data: UpdateGuestDTO): Promise<Guest> {
    const client = getApiClient();
    const apiGuest = await client.patch<ApiGuestResponse>(`v1/guests/${guestId}`, data);
    return mapApiGuestToGuest(apiGuest);
  }

  async getGuestQR(guestId: string): Promise<string> {
    const client = getApiClient();
    const response = await client.get<{ qrDataUrl: string }>(`v1/guests/${guestId}/qr`);
    return response.qrDataUrl;
  }
}

